import React, { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import FinanceInvoicePaymentModal from "./FinanceInvoicePaymentModal";
import { fetchCommissionInvoice } from "../reports/commissions/commissionsApi";
import { mapCommissionInvoiceToWizardRecord } from "../reports/commissions/commissionInvoiceRecordMapper";
import { applyCommissionInvoiceLineTotals } from "../reports/commissions/commissionInvoiceLineSuggestions";
import {
  normalizeFinanceInvoiceRecord,
  resolveFinanceInvoiceTransactions,
  resolveInvoiceDueAmount,
  resolveInvoiceLineItemsFromRecord,
} from "../../utilis/invoiceEditHelpers";
import {
  buildFinanceInvoicePaymentFormData,
  buildMultipartFormDataRequestConfig,
  getFinanceInvoicePaymentApiMessage,
  isFinanceInvoicePaymentApiSuccess,
  recordFinanceInvoicePaymentsSequentially,
} from "../../utilis/financeInvoicePaymentHelpers";
import { prepareInvoicePaymentLineItems } from "../../utilis/financeInvoicePaymentLineHelpers";
import { resolveInvoiceRecordPayableTotals } from "../../utilis/invoiceLineItemHelpers";
import { showCommissionInvoiceSuccessToast } from "../reports/commissions/commissionInvoiceToast";

function FinanceInvoiceQuickPayment({
  show = false,
  onClose,
  onSuccess,
  invoiceId = null,
  clientId = null,
  invoiceNumber = "",
  isCommission = false,
  multiLineHint = false,
  initialAmountDue = null,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [forceLineAllocation, setForceLineAllocation] = useState(false);

  const loadInvoice = useCallback(async () => {
    if (!invoiceId) {
      setInvoice(null);
      setLoadError("Invoice details are missing.");
      return;
    }

    setLoading(true);
    setLoadError("");
    try {
      if (isCommission) {
        const result = await fetchCommissionInvoice(invoiceId);
        const mapped = mapCommissionInvoiceToWizardRecord(result);
        if (!mapped?.id) {
          throw new Error("Commission invoice could not be loaded.");
        }
        setInvoice(
          applyCommissionInvoiceLineTotals(
            mapped,
            result?.linkedInstallments || [],
            result?.linkedBonusInstallments || [],
          ),
        );
        return;
      }

      const response = await apiUrl.get(`clientFinanceInvoices/${invoiceId}`);
      const mapped = normalizeFinanceInvoiceRecord(response);
      if (!mapped?.id) {
        throw new Error("Invoice could not be loaded.");
      }
      setInvoice(mapped);
    } catch (error) {
      setInvoice(null);
      setLoadError(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to load invoice for payment.",
      );
    } finally {
      setLoading(false);
    }
  }, [invoiceId, isCommission]);

  useEffect(() => {
    if (!show) {
      setInvoice(null);
      setLoadError("");
      setLoading(false);
      setForceLineAllocation(false);
      return;
    }
    setLoading(true);
    loadInvoice();
  }, [show, loadInvoice]);

  const paymentLineItems = useMemo(() => {
    if (!invoice) return [];
    return prepareInvoicePaymentLineItems(
      resolveInvoiceLineItemsFromRecord(invoice),
    );
  }, [invoice]);

  useEffect(() => {
    if (!show) return;
    if (multiLineHint || paymentLineItems.length > 1) {
      setForceLineAllocation(true);
    }
  }, [show, multiLineHint, paymentLineItems.length]);

  const transactions = useMemo(
    () => resolveFinanceInvoiceTransactions(invoice),
    [invoice],
  );

  /** Keep modal in loading state until invoice data arrives (avoids single-line flash). */
  const isLoadingInvoice =
    loading || (show && !invoice && !loadError);

  const amountDue = useMemo(() => {
    const listDue = Math.max(Number(initialAmountDue) || 0, 0);
    if (invoice) {
      const payable = resolveInvoiceRecordPayableTotals(invoice);
      const resolved = Math.max(
        payable.amount_due > 0
          ? payable.amount_due
          : resolveInvoiceDueAmount(invoice, payable.total_amount),
        0,
      );
      return resolved > 0 ? resolved : listDue;
    }
    return listDue;
  }, [invoice, initialAmountDue]);

  const currency = invoice?.currency || "AUD";
  const resolvedInvoiceNumber =
    invoiceNumber || invoice?.invoice_number || invoice?.invoice_no || "";

  const loadingWideLayout =
    multiLineHint || paymentLineItems.length > 1;

  const postPayment = async (
    payment,
    receiptFile = null,
    receiptFiles = null,
    options = {},
  ) => {
    const { silent = false } = options;
    const targetInvoiceId = invoice?.id;
    if (!targetInvoiceId) {
      Swal.fire("Error", "Invoice details are missing.", "error");
      return false;
    }

    try {
      setSaving(true);
      const formData = buildFinanceInvoicePaymentFormData({
        invoiceId: targetInvoiceId,
        clientId: clientId || invoice?.client_id,
        payment,
        receiptFile,
        receiptFiles,
      });

      const response = await apiUrl.post(
        `invoicepayment/${targetInvoiceId}`,
        formData,
        buildMultipartFormDataRequestConfig(),
      );

      if (!isFinanceInvoicePaymentApiSuccess(response)) {
        Swal.fire(
          "Error",
          getFinanceInvoicePaymentApiMessage(
            response,
            "Failed to record payment.",
          ),
          "error",
        );
        return false;
      }

      if (!silent) {
        showCommissionInvoiceSuccessToast(
          getFinanceInvoicePaymentApiMessage(
            response,
            "Payment recorded successfully.",
          ),
        );
      }

      if (!options?.skipRefresh) {
        await loadInvoice();
      }
      onSuccess?.(response);
      return true;
    } catch (error) {
      Swal.fire(
        "Error",
        error?.response?.data?.message ||
          error?.message ||
          "Failed to record payment.",
        "error",
      );
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleBulkPayment = async (entries) => {
    const saved = await recordFinanceInvoicePaymentsSequentially(
      entries,
      (payment, receiptFile, receiptFiles, options) =>
        postPayment(payment, receiptFile, receiptFiles, {
          ...options,
          skipRefresh: true,
        }),
    );

    if (saved) {
      await loadInvoice();
      onClose?.();
    }

    return saved;
  };

  const handleSinglePayment = async (payment, receiptFile, receiptFiles) => {
    const saved = await postPayment(payment, receiptFile, receiptFiles);
    if (saved) {
      onClose?.();
    }
    return saved;
  };

  const handleClose = () => {
    if (saving) return;
    onClose?.();
  };

  return (
    <FinanceInvoicePaymentModal
      show={show}
      onClose={handleClose}
      onSave={handleSinglePayment}
      onSaveBulk={handleBulkPayment}
      saving={saving}
      loading={isLoadingInvoice}
      loadError={loadError}
      loadingWideLayout={loadingWideLayout}
      forceLineAllocation={forceLineAllocation}
      currency={currency}
      amountDue={amountDue}
      invoiceNumber={resolvedInvoiceNumber}
      lineItems={paymentLineItems}
      existingTransactions={transactions}
    />
  );
}

export default FinanceInvoiceQuickPayment;
